'use client'

import { ChangeEvent, useCallback, useMemo, useState } from 'react'

import { Avatar, Button, DropdownMenu, Icon, SearchBar } from '@graftcode/design-system'

import { useProjectsStore } from '@common/modules/projects'
import { useUserStore } from '@common/modules/user/stores'

import { useAuthUser } from '../../hooks/useAuthUser'
import { HelpDrawer } from '../HelpDrawer'
import { HELP_ITEM_ID } from '../HelpMenu'
import { Logo } from '../Logo'
import { Selector } from '../Selector'
import { ShareFeedback } from '../ShareFeedback'

import { createProfileItems, createProjectItems } from './Header.helpers'
import { type HeaderProps } from './Header.types'
import { useAvatarImage } from './hooks'

import styles from './Header.module.scss'

export function Header(props: HeaderProps) {
  return <HeaderDefault {...props} />
}

function HeaderDefault(props: HeaderProps) {
  const {
    profileItems,
    helpItems,
    leftSlot,
    rightSlot,
    hideActions = false,
    searchDisabled = false,
    feedbackDisabled = false,
    helpDisabled = false,
    notificationsDisabled = false,
    projectSelectorLoading = false,
    projectSelectorDisabled = false,
    comingSoonLabel,
    feedbackPopoverOpen = false,
    onFeedbackPopoverOpenChange,
    onProfileClick,
    onHelpClick,
    onProjectSelect,
    onSearchChange,
    notificationBadge,
    onNotificationClick,
    onMenuToggle,
    searchSlot,
    as = 'div',
    rootClassName,
  } = props

  const Root = as === 'header' ? 'header' : 'div'
  const rootClassNames = [styles.wrapper, rootClassName].filter(Boolean).join(' ')

  const projects = useProjectsStore((s) => s.projects)
  const currentProject = useProjectsStore((s) => s.currentProject)
  const { currentUser } = useUserStore()
  const { user: auth0User } = useAuthUser()

  const projectItems = useMemo(() => createProjectItems(projects, currentProject), [projects, currentProject])
  const updatedProfileItems = useMemo(() => createProfileItems(profileItems, currentUser), [profileItems, currentUser])

  const loadedAvatarSrc = useAvatarImage(auth0User?.picture)

  const [searchValue, setSearchValue] = useState('')
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false)

  const handleSearchClear = useCallback(() => {
    setSearchValue('')
    onSearchChange?.('')
  }, [onSearchChange])

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      setSearchValue(value)
      onSearchChange?.(value)
    },
    [onSearchChange]
  )

  const handleDrawerToggle = () => onMenuToggle?.()

  const resolvedHelpItems = useMemo(() => {
    if (!helpItems || !feedbackDisabled) return helpItems

    return helpItems.map((item) =>
      item.type === 'item' && item.id === HELP_ITEM_ID.SHARE_FEEDBACK ? { ...item, active: false } : item
    )
  }, [feedbackDisabled, helpItems])

  const handleFeedbackPopoverOpenChange = useCallback(
    (open: boolean) => {
      if (feedbackDisabled && open) return
      onFeedbackPopoverOpenChange?.(open)
    },
    [feedbackDisabled, onFeedbackPopoverOpenChange]
  )

  // TODO: Implement search navigate
  const handleSearchNavigate = () => {}

  const searchbarClasses = [styles.searchbar, searchDisabled && styles.disabled].filter(Boolean).join(' ')

  const isHelpButtonDisabled = helpDisabled
  const helpDesktopClasses = [styles.helpDesktop, isHelpButtonDisabled && styles.disabled].filter(Boolean).join(' ')
  const helpMobileClasses = [styles.helpMobile, isHelpButtonDisabled && styles.disabled].filter(Boolean).join(' ')
  const hasHelpItems = !!resolvedHelpItems && resolvedHelpItems.length > 0
  const isHelpDropdownActive = hasHelpItems && !isHelpButtonDisabled

  const notificationClasses = [styles.notification, notificationsDisabled && styles.disabled].filter(Boolean).join(' ')

  const searchButtonClasses = [styles.search, searchDisabled && styles.disabled].filter(Boolean).join(' ')

  return (
    <Root className={rootClassNames}>
      <div className={styles.topbarLeft}>
        <a href="/" className={styles.logo}>
          <Logo />
        </a>

        {leftSlot ?? (
          <div className={styles.selector}>
            <Selector
              selectedName={currentProject?.name || 'Select Project'}
              items={projectItems}
              loading={projectSelectorLoading}
              disabled={projectSelectorDisabled}
              onSelect={onProjectSelect}
            />
          </div>
        )}
      </div>

      {hideActions && (rightSlot || onMenuToggle || searchSlot) && (
        <div className={styles.topbarRight}>
          {searchSlot}
          {rightSlot}
          {onMenuToggle && (
            <div className={styles.actions}>
              <div className={styles.navigation}>
                <Button
                  iconOnly
                  size="l"
                  variant="ghost"
                  iconPlacement="right"
                  icon={Icon.NavigationRegular}
                  onClick={handleDrawerToggle}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {!hideActions && (
        <div className={styles.topbarRight}>
          {searchSlot ? (
            <div className={styles.searchSlotWrap}>{searchSlot}</div>
          ) : (
            <div className={searchbarClasses}>
              <SearchBar
                size="md"
                shortcut="⌘N"
                version="white"
                value={searchValue}
                onChange={handleSearchChange}
                onClear={handleSearchClear}
                disabled={searchDisabled}
              />
              {searchDisabled && comingSoonLabel && <span className={styles.comingSoonLabel}>{comingSoonLabel}</span>}
            </div>
          )}

          <div className={styles.actions}>
            {!searchSlot && (
              <div className={searchButtonClasses}>
                <Button
                  iconOnly
                  size="l"
                  variant="ghost"
                  icon={Icon.SearchRegular}
                  onClick={handleSearchNavigate}
                  disabled={searchDisabled}
                />
              </div>
            )}

            <div className={helpDesktopClasses}>
              <ShareFeedback
                open={feedbackDisabled ? false : feedbackPopoverOpen}
                onOpenChange={handleFeedbackPopoverOpenChange}
                anchor={<span aria-hidden="true" className={styles.helpDesktopAnchor} data-share-feedback-anchor />}
              >
                {isHelpDropdownActive ? (
                  <DropdownMenu
                    width="s"
                    align="end"
                    side="bottom"
                    sideOffset={4}
                    items={resolvedHelpItems}
                    onItemClick={onHelpClick}
                  >
                    <Button
                      iconOnly
                      size="l"
                      variant="ghost"
                      iconPlacement="right"
                      icon={Icon.QuestionCircleRegular}
                      className={styles.helpInvoker}
                      data-help-invoker
                    />
                  </DropdownMenu>
                ) : (
                  <Button
                    iconOnly
                    size="l"
                    variant="ghost"
                    iconPlacement="right"
                    icon={Icon.QuestionCircleRegular}
                    className={styles.helpInvoker}
                    data-help-invoker
                    disabled={isHelpButtonDisabled}
                  />
                )}
              </ShareFeedback>
            </div>

            <div className={helpMobileClasses}>
              <Button
                iconOnly
                size="l"
                variant="ghost"
                iconPlacement="right"
                icon={Icon.QuestionCircleRegular}
                className={styles.helpInvoker}
                data-help-invoker
                disabled={isHelpButtonDisabled || !hasHelpItems}
                onClick={() => setIsHelpDrawerOpen(true)}
              />
            </div>

            <div className={notificationClasses}>
              <div className={styles.notificationBtn}>
                <Button
                  iconOnly
                  size="l"
                  variant="ghost"
                  iconPlacement="right"
                  icon={Icon.AlertRegular}
                  onClick={onNotificationClick}
                  disabled={notificationsDisabled}
                />
                {!!notificationBadge && notificationBadge > 0 ? (
                  <span className={styles.notificationBadge} aria-hidden="true">
                    {notificationBadge}
                  </span>
                ) : null}
              </div>
            </div>

            <div className={styles.navigation}>
              <Button
                iconOnly
                size="l"
                variant="ghost"
                iconPlacement="right"
                icon={Icon.NavigationRegular}
                onClick={handleDrawerToggle}
              />
            </div>
          </div>

          <div className={styles.profile}>
            <DropdownMenu
              width="s"
              align="end"
              side="bottom"
              sideOffset={4}
              items={updatedProfileItems}
              onItemClick={onProfileClick}
            >
              <div className={styles.avatar}>
                <Avatar size={32} src={loadedAvatarSrc} />
              </div>
            </DropdownMenu>
          </div>
        </div>
      )}

      {hasHelpItems && (
        <HelpDrawer
          isOpen={isHelpDrawerOpen}
          onClose={() => setIsHelpDrawerOpen(false)}
          items={resolvedHelpItems ?? []}
          onItemClick={onHelpClick}
        />
      )}
    </Root>
  )
}
